import React, { useEffect, useState } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { useDispatch } from 'react-redux'
import { Box, Grid, Link } from '@mui/material'
import { Outlet, useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { grey } from '@mui/material/colors'
import { useTranslation } from 'react-i18next'

import { getAssetURL } from 'lib/utility/GetAssetUrl'
import { fetchInitialData } from 'api/actions/Setup'
import Sidebar from 'containers/sidebar/Sidebar'
import { useAppSelector } from 'redux/Hook'
import { setI18nLocale } from 'lib/utility/LocaleHelper'
import Alert from 'components/Shared/Alert'
import MobileSidebar from 'components/Sidebar/MobileSidebar'
import {
  initializeSegment,
  trackCurrentPage,
  trackCurrentUser,
  trackGroup,
} from 'lib/segmentTracker/Segment'
import { setReportingDetails } from 'redux/slices/ShareReportingDetails'
import { intercomInitialiser, intercomNode } from 'lib/IntercomTracker/Intercom'
import usePrevious from 'lib/hooks/UsePrevious'
import { Participant } from 'lib/authorization/Roles'

const AdminLayout = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [fetchedInitialData, setFetchedInitialData] = useState(false)
  const {
    user: { user },
    features: {
      features,
      features: {
        segment_analytics_admin: { enabled: isSegmentdEnabled = false } = {},
      },
    },
    site: { site },
    libraraiesDetails: { intercom = {} },
  } = useAppSelector((state) => state)

  const location = useLocation()
  const prevProps = usePrevious({ user, features, site, location })

  useEffect(() => {
    fetchInitialData(dispatch).then(() => setFetchedInitialData(true))
  }, [])

  useEffect(() => {
    const updateSegmentTracker = () => {
      const checkIfEqualData = () =>
        prevProps &&
        !isEmpty(features) &&
        (JSON.stringify(prevProps?.features) !== JSON.stringify(features) ||
          prevProps.location.pathname !== location.pathname)

      if (checkIfEqualData() && isSegmentdEnabled) {
        trackCurrentPage(user, site, isSegmentdEnabled)
      }
    }
    updateSegmentTracker()
  }, [location, features])

  useEffect(() => {
    if (isSegmentdEnabled && !isEmpty(site)) {
      trackCurrentUser(user, site, isSegmentdEnabled)
      trackGroup(site, isSegmentdEnabled)
    }
  }, [features, site])

  useEffect(() => {
    // For participant, lang will be set from site data
    if (user.roleName === Participant) {
      setI18nLocale(site.locale)
    } else {
      user.adminLocale && setI18nLocale(user.adminLocale)
    }
  }, [user, site])

  useEffect(() => {
    const updateSegmentTracker = () => {
      const checkIfEqualData = (item: any) =>
        prevProps &&
        isEmpty(item) &&
        JSON.stringify(prevProps[`${item as keyof typeof prevProps}`]) !==
          JSON.stringify(item)

      if (checkIfEqualData(features)) {
        features?.['segment_analytics_admin']?.enabled &&
          trackCurrentPage(
            user,
            site,
            features?.['segment_analytics_admin']?.enabled,
          )
      }
    }
    updateSegmentTracker()
  }, [features])

  useEffect(() => {
    const {
      sharedUrl: { link, password, id },
    }: any = site

    dispatch(
      setReportingDetails({
        id,
        link,
        password,
      }),
    )
  }, [site])

  const isReportingPage = location.pathname === '/adm'
  const isTextAnalysisPage = location.pathname.startsWith('/adm/text_analysis')
  const isToolBuilderPage = location.pathname.startsWith('/adm/tool_builder')

  const bgGreyPages = isReportingPage || isTextAnalysisPage

  const wrapperStyles = bgGreyPages && {
    bgcolor: grey[100],
  }

  const Component = (
    <>
      <HelmetProvider>
        <Helmet>
          <script>{initializeSegment()}</script>
          <link
            rel="icon"
            type="image/x-icon"
            href={`${getAssetURL('/sidebar/favicon.ico')}`}
          ></link>
          <script>{intercomInitialiser()}</script>
        </Helmet>
      </HelmetProvider>
      <Box>
        <Link
          href="#main-content"
          sx={{
            position: 'absolute',
            zIndex: '1000',
            left: '-1em',
            top: '-1.4em',
            overflow: 'hidden',
            boxSizing: 'border-box',
            '&:focus': {
              top: '1.4em',
              left: '1.4em',
              width: '10em',
              height: '3.5em',
              borderRadius: '0.375rem',
              borderStyle: 'solid',
              fontWeight: 700,
              backgroundColor: '#FFF',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: 'center',
              textDecoration: 'underline',
              outlineOffset: 0,
              outline: '3px solid rgba(66, 153, 225, 0.6)',
            },
          }}
        >
          {t('common.skipToContent')}
        </Link>
        <MobileSidebar />
        <Box sx={{ display: 'flex' }} id="adminLayout">
          <Sidebar />
          <Alert />
          <Box
            component="main"
            id="main-content"
            sx={{ flexGrow: 1, ...wrapperStyles }}
          >
            <Grid container>
              <Grid
                container
                sx={{
                  px: {
                    xs: 2,
                    sm: 5,
                  },
                  py: {
                    xs: 2,
                    sm: 4,
                  },
                }}
              >
                <Outlet />
              </Grid>
            </Grid>
          </Box>
        </Box>

        {intercomNode({
          intercom,
          user,
        })}
      </Box>
    </>
  )

  if (fetchedInitialData && isToolBuilderPage) {
    return <Outlet />
  }
  return fetchedInitialData ? Component : <></>
}

export default AdminLayout
